'use client';

import { initFaro, faroEnabled } from '@/lib/telemetry/faro';
import { isPlainObject, resolveError } from '@/lib/log-format';
import type { LogContext } from '@/lib/log-format';
import type { LogLevel } from '@grafana/faro-web-sdk';

const isDevelopment = process.env.NODE_ENV === 'development';

type ClientLogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

type ConsoleMethod = 'debug' | 'log' | 'warn' | 'error';

const LOG_METHOD: Record<ClientLogLevel, ConsoleMethod> = {
  DEBUG: 'debug',
  INFO: 'log',
  WARN: 'warn',
  ERROR: 'error',
};

const FARO_LEVEL: Record<ClientLogLevel, LogLevel> = {
  DEBUG: 'debug' as LogLevel,
  INFO: 'info' as LogLevel,
  WARN: 'warn' as LogLevel,
  ERROR: 'error' as LogLevel,
};

const stringifyContext = (context: LogContext): Record<string, string> => {
  const entries = Object.entries(context).map(([key, value]) => {
    if (typeof value === 'string') {
      return [key, value];
    }
    try {
      return [key, JSON.stringify(value)];
    } catch {
      return [key, String(value)];
    }
  });
  return Object.fromEntries(entries);
};

const toConsoleEntry = (
  level: ClientLogLevel,
  message: string,
  fields: LogContext,
): LogContext => ({
  level,
  message,
  timestamp: new Date().toISOString(),
  ...fields,
});

/**
 * Client-side logger. When Grafana Faro is enabled (NEXT_PUBLIC_FARO_URL is
 * set and the SDK finished initializing) it forwards each call to Faro;
 * otherwise it falls back to the same structured console output the previous
 * isomorphic logger produced in the browser. Local console output is kept in
 * both cases so debugging works offline.
 */
export const logger = {
  debug: (message: string, context?: LogContext): void => {
    if (!isDevelopment) {
      return;
    }
    emit('DEBUG', message, context);
  },

  info: (message: string, context?: LogContext): void => {
    emit('INFO', message, context);
  },

  warn: (message: string, context?: LogContext): void => {
    emit('WARN', message, context);
  },

  error: (
    message: string,
    errorOrContext?: unknown,
    context?: LogContext,
  ): void => {
    let error = errorOrContext;
    let ctx = context;

    if (isPlainObject(errorOrContext) && !context) {
      // Convenience form: logger.error('msg', { status, path }) logs metadata only.
      ctx = errorOrContext;
      error = undefined;
    }

    const { context: errorContext, fields, errorObject } = resolveError(error);
    emit('ERROR', message, { ...errorContext, ...fields, ...ctx }, errorObject);
  },
};

const emit = (
  level: ClientLogLevel,
  message: string,
  context?: LogContext,
  error?: unknown,
): void => {
  const entry = toConsoleEntry(level, message, context ?? {});
  console[LOG_METHOD[level]](entry);

  if (faroEnabled()) {
    void initFaro().then((faro) => {
      if (!faro) {
        return;
      }
      const faroContext = stringifyContext(context ?? {});
      if (level === 'ERROR' && error instanceof Error) {
        faro.api.pushError(error, { context: faroContext });
        return;
      }
      faro.api.pushLog([message], {
        level: FARO_LEVEL[level],
        context: faroContext,
      });
    });
  }
};
